import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MdSearch, MdAdd, MdFacebook, MdEmail } from 'react-icons/md';
import { FaInstagram, FaTiktok, FaWhatsapp, FaSms } from 'react-icons/fa';
import IntegrationCard from './IntegrationCard';
import IntegrationDetail from './IntegrationDetail';
import { fetchIntegrationsRequest } from '../../redux/slices/integrationsSlice';
import { getIntegrationStatus } from '../../utils/oauthHelper';

import type { RootState } from '../../redux/store';
import type { Integration, IntegrationStatus } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

type FilterType = 'all' | 'connected' | 'disconnected';

const Integrations: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  
  // Get integrations from Redux store
  const { data: apiIntegrations, loading } = useSelector(
    (state: RootState) => state.integrations
  );

  // Fetch integrations on mount
  useEffect(() => {
    if (!restaurantId) return;
    dispatch(fetchIntegrationsRequest({ restaurantId }));
  }, [dispatch, restaurantId]);

  // Define available integrations with UI data
  const availableIntegrations: Integration[] = [
    {
      id: '1',
      type: 'facebook',
      name: 'Facebook',
      description: 'Connect your Facebook account to automatically sync and share your campaigns across your Facebook page and business account.',
      icon: <MdFacebook />,
      color: '#1877F2',
      status: 'connected'
    },
    {
      id: '2',
      type: 'instagram',
      name: 'Instagram',
      description: 'Integrate Instagram to share your flyers and campaigns directly to your Instagram feed and stories with one click.',
      icon: <FaInstagram />,
      color: '#E4405F',
      status: 'connected'
    },
    {
      id: '3',
      type: 'tiktok',
      name: 'TikTok',
      description: 'Share your campaigns on TikTok to reach a wider audience and engage with trending content seamlessly.',
      icon: <FaTiktok />,
      color: '#000000',
      status: 'disconnected'
    },
    {
      id: '4',
      type: 'email',
      name: 'Email',
      description: 'Send campaign emails directly to your subscribers with beautifully designed templates and automated scheduling.',
      icon: <MdEmail />,
      color: '#EA4335',
      status: 'disconnected'
    },
    {
      id: '5',
      type: 'whatsapp',
      name: 'WhatsApp',
      description: 'Connect WhatsApp Business to send campaign messages, promotional content, and updates to your customers directly.',
      icon: <FaWhatsapp />,
      color: '#25D366',
      status: 'disconnected',
      comingSoon: true
    },
    {
      id: '6',
      type: 'sms',
      name: 'SMS',
      description: 'Send campaign notifications and promotional messages via SMS to reach customers instantly on their mobile devices.',
      icon: <FaSms />,
      color: '#EE4423',
      status: 'disconnected',
      comingSoon: true
    }
  ];

  // Merge API data with UI definitions - memoized to prevent infinite loop
  const integrations: Integration[] = useMemo(() => {
    return availableIntegrations.map((uiIntegration) => {
      const { isActive } = getIntegrationStatus(apiIntegrations, uiIntegration.type);
      return {
        ...uiIntegration,
        status: isActive ? 'connected' : 'disconnected'
      };
    });
  }, [apiIntegrations]);

  // Check URL for selected integration on mount only (not on every location change)
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length > 2 && pathParts[1] === 'integrations' && !selectedIntegration) {
      const integrationName = pathParts[2];
      const integration = integrations.find(i => i.type === integrationName);
      if (integration) {
        setSelectedIntegration(integration);
      }
    }
  }, [location.pathname, integrations, selectedIntegration]);

  // handleToggle is now handled in IntegrationDetail via Redux actions

  const handleIntegrationClick = (integration: Integration) => {
    // Don't allow clicking on coming soon integrations
    if (integration.comingSoon) {
      return;
    }
    
    // Set selected integration immediately (don't wait for URL effect)
    setSelectedIntegration(integration);
    
    // Update URL for browser history
    navigate(`/integrations/${integration.type}`, { replace: false });
  };

  const handleBack = () => {
    setSelectedIntegration(null);
    navigate('/integrations', { replace: false });
  };


  const filteredIntegrations = integrations.filter(integration => {
    // Filter by status
    if (filter === 'connected' && integration.status !== 'connected') return false;
    if (filter === 'disconnected' && integration.status !== 'disconnected') return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        integration.name.toLowerCase().includes(query) ||
        integration.description.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const connectedCount = integrations.filter(i => i.status === 'connected').length;
  const disconnectedCount = integrations.filter(i => i.status === 'disconnected').length;

  // Show detail view if integration is selected
  if (selectedIntegration) {
    return <IntegrationDetail integration={selectedIntegration} onBack={handleBack} />;
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-black p-4"
    >
      <div className="">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-medium text-[#EE4423] mb-2">Integrations</h1>
            <p className="text-gray-400 text-sm">
              Connect your favorite social media to Promoflow
            </p>
          </div>
          
        </div>

        {/* Filters and Search */}
        <div className="flex items-center gap-4 mb-8 mt-8">
          {/* Filter Tabs */}
          <div className="flex items-center gap-2 rounded-lg p-1 border border-gray-800"
          style={{background: "linear-gradient(90deg,rgba(33,33,33,0.92),rgba(55,55,55,0.92))"}}>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-[#616161] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All Integrations
            </button>
            <button
              onClick={() => setFilter('connected')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${
                filter === 'connected'
                  ? 'bg-[#616161] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Connected ({connectedCount})
            </button>
            <button
              onClick={() => setFilter('disconnected')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${
                filter === 'disconnected'
                  ? 'bg-[#616161] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Disconnected ({disconnectedCount})
            </button>
          </div>
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredIntegrations.map((integration, index) => (
            <motion.div
              key={integration.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              onClick={() => handleIntegrationClick(integration)}
              className="cursor-pointer"
            >
              <IntegrationCard
                integration={integration}
              />
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {filteredIntegrations.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No integrations found matching your criteria</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Integrations;
